/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

/**
 * MA6200-G1 Hierarchy Builder
 * Parses MA6200-G1 DAS inventory data and builds the topology hierarchy
 * 
 * ObjectKey Format: {controllerSerial}.{deviceType}.{deviceSerial}
 * Topology Format: {route} (e.g. 0.0.0.0)
 * 
 * Route-based Hierarchy:
 * - 0.0.0.0: Root (Primary A2) - label: "MA6200-G1"
 * - 5.0.0.0: Connected to root port 5 - label: "N2-RU.5" (equipmentType.port)
 * - 5.2.0.0: Connected to 5.0.0.0 port 2 - label: "N2-RU.2"
 * - 5.2.3.0: Connected to 5.2.0.0 port 3 - label: "E2-O.3"
 * - 1.0.5.6: Connected to 1.0.5.0 port 6 - label: "N2-RU.6"
 * 
 * A device with N non-zero octets is the child of the device with N-1 non-zero octets
 * where all N-1 octets match.
 * 
 * Labels use the full equipment type and last non-zero octet as port (dot-separated):
 * - Primary A2 (root) -> "MA6200-G1"
 * - N2-RU + port 3 -> "N2-RU.3"
 * - M2-RU + port 5 -> "M2-RU.5"
 * - E2-O + port 3  -> "E2-O.3"
 * - Secondary A2   -> "AU2.2" (or actual devType + port)
 */

import { IInventoryGraphNode, IRemoteEquipment } from "../../../store/api/devices/types";
import { TopologyNode, DasNodeType, DasNodeStatus, HierarchyBuilder } from "../types";

interface ObjectKeyParts {
  targetSerial: string;
  devId?: string;
  route?: string;
}

export class MA6200G1HierarchyBuilder implements HierarchyBuilder {
  private equipmentMap: Map<string, IRemoteEquipment>;
  private processedNodes: Set<string>; // Track nodes already added to tree

  constructor() {
    this.equipmentMap = new Map();
    this.processedNodes = new Set();
  }

  /**
   * Set equipment data for additional details
   */
  setEquipmentData(equipment: IRemoteEquipment[]) {
    this.equipmentMap.clear();
    
    console.log('[MA6200-G1] Setting equipment data:', equipment.length, 'items');
    const sampleEquipment = equipment.slice(0, 3);
    console.log('[MA6200-G1] Sample equipment (first 3):', sampleEquipment.map(e => ({
      objectKey: e.objectKey,
      equipmentType: e.equipmentType,
      hostname: e.hostname
    })));
    
    equipment.forEach((eq) => {
      // Index by objectKey (primary)
      if (eq.objectKey) {
        this.equipmentMap.set(eq.objectKey, eq);
      }
      // Also index by serial number for fallback
      if (eq.serialNumber) {
        this.equipmentMap.set(eq.serialNumber, eq);
      }
    });
    
    console.log('[MA6200-G1] Equipment map size:', this.equipmentMap.size);
  }

  /**
   * Get equipment details for a node
   */
  private getEquipment(node: IInventoryGraphNode): IRemoteEquipment | undefined {
    // Try multiple lookup strategies
    let equipment = this.equipmentMap.get(node.id);
    if (equipment) return equipment;
    
    equipment = this.equipmentMap.get(node.label);
    if (equipment) return equipment;
    
    return undefined;
  }

  /**
   * Parse objectKey string into components
  * Format: {controllerSerial}.{deviceType}.{deviceSerial}
   */
  private parseObjectKey(objectKey: string): ObjectKeyParts {
    if (!objectKey) {
      return {
        targetSerial: '',
      };
    }

    const parts = objectKey.split(".");
    
    const result: ObjectKeyParts = {
      targetSerial: parts[0] || '',
    };

    if (parts.length > 2) {
      result.devId = parts[2];
    }

    const equipment = this.equipmentMap.get(objectKey);
    if (equipment?.topology) {
      result.route = equipment.topology;
    }

    return result;
  }

  /**
   * Check if this is the primary A2 (head of tree)
   * Primary A2 is identified by route=0.0.0.0
   */
  private isPrimaryA2(objectKey: string, equipmentType: string): boolean {
    const parts = this.parseObjectKey(objectKey);
    return parts.route === "0.0.0.0" && equipmentType.toUpperCase().includes("A2");
  }

  /**
   * Parse status from API status string
   */
  parseStatus(status: string): DasNodeStatus {
    const statusLower = (status || "").toLowerCase();
    
    switch (statusLower) {
      case "connected":
      case "online":
      case "normal":
        return DasNodeStatus.ONLINE;
      case "disconnected":
      case "offline":
        return DasNodeStatus.DISCONNECTED;
      case "clear":
      case "cleared":
        return DasNodeStatus.CLEAR;
      case "minor":
        return DasNodeStatus.MINOR;
      case "major":
      case "warning":
        return DasNodeStatus.MAJOR;
      case "critical":
      case "error":
        return DasNodeStatus.CRITICAL;
      default:
        return DasNodeStatus.UNKNOWN;
    }
  }

  /**
   * Parse node type from equipment type
   */
  parseNodeType(node: IInventoryGraphNode): DasNodeType {
    const equipmentType = node.equipmentType || "";
    const normalized = equipmentType?.toUpperCase() || "";
    
    if (normalized.includes("AU2") || normalized === "A2") {
      return DasNodeType.ACM; // Use ACM as the primary unit display type
    }
    if (normalized.includes("RU2") || normalized === "RU" || normalized === "RUE") {
      return DasNodeType.RAU; // Remote units
    }
    if (normalized.includes("E2-O") || normalized === "E2O") {
      return DasNodeType.OCH; // Optical modules
    }
    
    return DasNodeType.SITE;
  }

  /**
   * Create topology node from equipment
   */
  private createTopologyNode(equipment: IRemoteEquipment, type: DasNodeType, isRoot: boolean = false): TopologyNode {
    let label = "";
    
    if (isRoot) {
      // Root (primary A2) shows "MA6200-G1"
      label = "MA6200-G1";
      console.log('[MA6200-G1] Creating ROOT node with label:', label);
    } else {
      // For children, use full equipment type + dot + port number from route
      // Example: "N2-RU.5", "E2-O.3", "A2.2"
      if (equipment.objectKey) {
        const parts = this.parseObjectKey(equipment.objectKey);
        if (parts.route) {
          // Find the last non-zero octet for the port number
          const routeParts = parts.route.split(".");
          let port = null;
          for (let i = routeParts.length - 1; i >= 0; i--) {
            if (routeParts[i] !== "0") {
              port = routeParts[i];
              break;
            }
          }
          
          if (port) {
            const equipmentType = equipment.equipmentType || "DEVICE";
            label = `${equipmentType}.${port}`;
            console.log(`[MA6200-G1] Creating node for ${equipment.objectKey}: label="${label}", type="${equipmentType}", route="${parts.route}"`);
          }
        }
      }
      
      // Fallback: use only equipment type
      if (!label) {
        label = equipment.equipmentType || "Unknown";
      }
    }
    
    return {
      id: equipment.objectKey || equipment.serialNumber || "",
      label, // This is the ONLY text that should show in the box
      type,
      equipmentType: equipment.equipmentType || "",
      status: this.parseStatus(equipment.status || ""),
      model: equipment.model,
      ipAddress: equipment.ipAddress,
      serialNumber: equipment.serialNumber,
      managedObject: equipment.objectKey,
      children: [],
      metadata: {
        equipment,
      },
    };
  }

  /**
   * Build MA6200-G1 hierarchy from equipment list
   * Hierarchy:
   * - Primary A2 (root) - devId=0, route=0.0.0.0
   *   - Other modules (RU2, E2-O, etc.) - children based on parent relationships
   */
  buildHierarchy(_nodes: IInventoryGraphNode[], _edges: any[], rootId: string): TopologyNode | null {
    const equipment = Array.from(this.equipmentMap.values());
    
    // Clear processed nodes for fresh hierarchy build
    this.processedNodes.clear();
    
    if (equipment.length === 0) {
      console.log('[MA6200-G1] No equipment data available');
      return null;
    }

    console.log('[MA6200-G1] Building hierarchy from', equipment.length, 'equipment items');

    // Find the primary A2 - the one with route=0.0.0.0
    const primaryA2 = equipment.find((eq) => 
      eq.objectKey && this.isPrimaryA2(eq.objectKey, eq.equipmentType || "")
    );

    if (!primaryA2) {
      console.log('[MA6200-G1] No device with route 0.0.0.0 found');
      return null;
    }

    console.log('[MA6200-G1] Found primary A2:', primaryA2.objectKey);

    // Create root node from primary A2 with SITE type and label "MA6200-G1"
    const root = this.createTopologyNode(primaryA2, DasNodeType.SITE, true);
    
    // Mark root as processed
    if (primaryA2.objectKey) {
      this.processedNodes.add(primaryA2.objectKey);
    }
    
    // Build children hierarchy
    this.buildChildren(root, equipment);

    console.log('[MA6200-G1] Hierarchy built with', root.children.length, 'direct children');
    
    return root;
  }

 /**
 * Determines whether a given child route is a direct child of a parent route.
 *
 * Rules:
 * - Child must match all non-zero levels of the parent.
 * - Child must introduce exactly ONE new non-zero level.
 * - The new level must appear AFTER the parent’s deepest non-zero level.
 *
 *   Examples:
 * - Parent: 0.0.0.0, Child: 5.0.0.0 → true  (root → first level)
 * - Parent: 5.0.0.0, Child: 5.2.0.0 → true  (next level under same branch)
 * - Parent: 1.0.5.0, Child: 1.0.5.6 → true  (deeper level added at index 3)
 * - Parent: 1.0.0.0, Child: 1.0.13.0 → true (sparse depth, still valid direct child)
 * - Parent: 1.1.0.0, Child: 1.1.1.0 → true  (next depth)
 * - Parent: 1.0.1.0, Child: 1.1.1.0 → false (branch mismatch at index 1)
 * - Parent: 5.0.0.0, Child: 5.2.3.0 → false (adds more than one new level)
 * - Parent: 5.0.0.0, Child: 3.0.0.0 → false (different root branch)
 * - Parent: 1.2.0.0, Child: 1.3.0.0 → false (parent level changed)
 * - Parent: 1.2.0.0, Child: 1.2.0.0 → false (same route, not a child)
 */
  private isDirectChild(parentRoute: string, childRoute: string): boolean {
  if (!parentRoute || !childRoute || parentRoute === childRoute) return false;

  const parentOctets = parentRoute.split(".");
  const childOctets = childRoute.split(".");

  if (parentOctets.length !== 4 || childOctets.length !== 4) return false;

  let parentLastNonZeroIndex = -1;   // deepest level used in parent
  let childNewLevelIndex = -1;       // index where child introduces new level
  let childNewLevelCount = 0;        // how many new levels child adds

  for (let index = 0; index < 4; index++) {
    const parentValue = parentOctets[index];
    const childValue = childOctets[index];

    // Parent has a value → child must match exactly
    if (parentValue !== "0") {
      if (parentValue !== childValue) return false;
      parentLastNonZeroIndex = index;
      continue;
    }

    // Parent is empty but child has value → new level introduced
    if (childValue !== "0") {
      childNewLevelCount++;
      if (childNewLevelIndex === -1) {
        childNewLevelIndex = index;
      }
    }
  }

  // Child must introduce exactly one new level
  if (childNewLevelCount !== 1) return false;

  // New level must be deeper than parent’s last level
  return childNewLevelIndex > parentLastNonZeroIndex;
}

  /**
   * Build children hierarchy based on route structure
   */
  private buildChildren(parent: TopologyNode, equipment: IRemoteEquipment[]) {
    const parentObjectKey = parent.managedObject;
    if (!parentObjectKey) return;

    const parentParts = this.parseObjectKey(parentObjectKey);
    if (!parentParts.route) return;
    
    // Find direct children based on route hierarchy
    const children = equipment.filter((eq) => {
      // Skip self
      if (eq.objectKey === parentObjectKey) return false;
      
      if (eq.objectKey) {
        const childParts = this.parseObjectKey(eq.objectKey);
        
        // Must have same target serial
        if (childParts.targetSerial !== parentParts.targetSerial) return false;
        
        // Check if it's a direct child based on route
        if (childParts.route && parentParts.route) {
          return this.isDirectChild(parentParts.route, childParts.route);
        }
      }
      
      return false;
    });

    console.log(`[MA6200-G1] Found ${children.length} children for ${parentObjectKey} (route: ${parentParts.route})`);
    
    if (children.length > 0) {
      const childRoutes = children.map(c => this.parseObjectKey(c.objectKey || "").route).join(", ");
      console.log(`[MA6200-G1] Child routes: ${childRoutes}`);
    }

    children.forEach((childEq) => {
      // Skip if already processed to prevent duplicates
      if (!childEq.objectKey || this.processedNodes.has(childEq.objectKey)) {
        return;
      }
      
      // Mark as processed
      this.processedNodes.add(childEq.objectKey);
      
      const dummyNode: IInventoryGraphNode = {
        id: childEq.objectKey || "",
        label: childEq.hostname || "",
        equipmentType: childEq.equipmentType || "",
      };
      const nodeType = this.parseNodeType(dummyNode);
      const childNode = this.createTopologyNode(childEq, nodeType);
      parent.children.push(childNode);
      
      // Recursively build children for this node
      this.buildChildren(childNode, equipment);
    });

    // Sort children by port number (from route) for consistent display
    parent.children.sort((a, b) => {
      const aKey = a.managedObject || "";
      const bKey = b.managedObject || "";
      const aParts = this.parseObjectKey(aKey);
      const bParts = this.parseObjectKey(bKey);
      
      // Extract port numbers from routes
      if (aParts.route && bParts.route) {
        const aOctets = aParts.route.split(".");
        const bOctets = bParts.route.split(".");
        
        // Compare octets in order
        for (let i = 0; i < 4; i++) {
          const aVal = parseInt(aOctets[i] || "0");
          const bVal = parseInt(bOctets[i] || "0");
          if (aVal !== bVal) {
            return aVal - bVal;
          }
        }
      }
      
      return aKey.localeCompare(bKey);
    });
  }
}

// Export singleton instance
export const ma6200g1HierarchyBuilder = new MA6200G1HierarchyBuilder();
